import { useCallback, useEffect, useState } from 'react';
import type { AdminInvoiceRow, AdminListResponse } from '../../../domain/superadmin/types';
import {
  EMPTY_INVOICE_FILTERS,
  type AdminInvoiceFilters,
} from '../../../domain/superadmin/invoiceFilters';
import { useAppContext } from '../../../contexts/AppContext';
import { fetchAdminInvoices } from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';

const PAGE_SIZE = 25;

export function useAdminInvoices() {
  const [data, setData] = useState<AdminListResponse<AdminInvoiceRow> | null>(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState<AdminInvoiceFilters>(EMPTY_INVOICE_FILTERS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setCurrentPage } = useAppContext();

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAdminInvoices({
        page,
        pageSize: PAGE_SIZE,
        q: q || undefined,
        status: filters.status || undefined,
      });
      setData(result);
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        setCurrentPage('dashboard');
        notify.warning('Acesso não autorizado à área de superadmin.');
        return;
      }
      setError(e instanceof Error ? e.message : 'Erro ao carregar faturas');
    } finally {
      setIsLoading(false);
    }
  }, [page, q, filters, setCurrentPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const patchFilters = useCallback((patch: Partial<AdminInvoiceFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  return {
    data,
    isLoading,
    error,
    refetch: load,
    page,
    setPage,
    q,
    setQ,
    filters,
    patchFilters,
    pageSize: PAGE_SIZE,
  };
}
